/**
 * Shared date/time formatting utilities for open-house-dates widget.
 */
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import { parseISO } from 'date-fns/parseISO';

/** "Monday, 9th April" */
export function formatDateWithOrdinal(dateString: string): string {
  const date = parseISO(dateString);
  return format(date, 'EEEE, do MMMM');
}

/** "Monday, April 9, 2026" */
export function formatDateFull(dateString: string): string {
  const date = parseISO(dateString);
  return format(date, 'EEEE, MMMM d, yyyy');
}

/** "02:42AM - 05:42PM" (compact, no space before AM/PM) */
export function formatTimeRangeCompact(
  startDate: string,
  endDate: string,
): string {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  return `${format(start, 'hh:mmaa')} - ${format(end, 'hh:mmaa')}`;
}

/** "2:42 AM - 5:42 PM" (natural, with space before AM/PM) */
export function formatTimeRangeFull(
  startDate: string,
  endDate: string,
): string {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  return `${format(start, 'h:mm aa')} - ${format(end, 'h:mm aa')}`;
}

/** "Monday, 9th April, 02:42AM - 05:42PM" (for event cards) */
export function formatEventCardDateTime(
  startDate: string,
  endDate: string,
): string {
  return `${formatDateWithOrdinal(startDate)}, ${formatTimeRangeCompact(startDate, endDate)}`;
}

/** Convert ISO string to date input value "YYYY-MM-DD" */
export function toDateString(isoString: string | undefined): string {
  if (!isoString) return '';
  return format(parseISO(isoString), 'yyyy-MM-dd');
}

/** Convert ISO string to time input value "HH:MM" */
export function toTimeString(isoString: string | undefined): string {
  if (!isoString) return '';
  return format(parseISO(isoString), 'HH:mm');
}

/** Combine "YYYY-MM-DD" and "HH:MM" into ISO string */
export function combineDateAndTime(dateStr: string, timeStr: string): string {
  if (!dateStr || !timeStr) return '';
  const date = parse(`${dateStr}T${timeStr}`, "yyyy-MM-dd'T'HH:mm", new Date());
  return date.toISOString();
}

/** Format 24h "HH:MM" to display "HH:MM AM/PM" */
export function formatTimeOverlay(time: string): string {
  if (!time) return '';
  const date = parse(time, 'HH:mm', new Date());
  return format(date, 'hh:mm aa');
}

/** Format date input value "YYYY-MM-DD" to display "9th April" */
export function formatDateOverlay(dateStr: string): string {
  if (!dateStr) return '';
  const date = parse(dateStr, 'yyyy-MM-dd', new Date());
  return format(date, 'do MMMM');
}
